// file: src/utils/errorUtils.js
/**
 * Utility for displaying user-friendly error and success messages
 * Uses the ErrorBanner and SuccessBanner components for consistent theming with the landing page
 */

import { showErrorBanner, showSuccessBanner } from '../components/common/ErrorBannerManager';

// Extract a friendly message from an error object or string
export function getFriendlyErrorMessage(error) {
  if (!error) {
    return 'An unknown error occurred';
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle Error objects or API errors
  if (error.message) {
    return error.message;
  }

  // Handle axios response errors
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const serverMessage = error.response.data?.message || error.response.data?.retmsg;
    if (serverMessage) {
      return serverMessage;
    }
    
    // If no specific message, use status text
    switch (error.response.status) {
      case 401:
        return 'You need to login again to continue';
      case 403:
        return 'You don\'t have permission to perform this action';
      case 404:
        return 'The requested resource was not found';
      case 500:
        return 'The server encountered an error. Please try again later';
      default:
        return `Error: ${error.response.status} ${error.response.statusText}`;
    }
  }

  // Handle network errors
  if (error.request) {
    // The request was made but no response was received
    return 'Network error: Could not connect to the server';
  }

  // Generic fallback
  return 'An unexpected error occurred';
}

// Display an error using the themed ErrorBanner component
export function showError(error, title = 'Error', options = {}) {
  const message = getFriendlyErrorMessage(error);
  console.error(title, error);
  
  // Use the new error banner system
  return showErrorBanner(
    { message },
    {
      title,
      position: 'top-right',
      autoHide: true,
      autoHideDelay: 7000, // Slightly longer for errors
      ...options
    }
  );
}

/**
 * Wrap any async function with error handling
 * Usage: 
 *   const safeFunction = withErrorHandling(originalFunction);
 *   await safeFunction(...args);
 */
export function withErrorHandling(func, errorHandler = showError) {
  return async (...args) => {
    try {
      return await func(...args);
    } catch (error) {
      errorHandler(error);
      throw error; // Re-throw so calling code can still handle it if needed
    }
  };
}

/**
 * An error boundary-like function for async operations
 * Usage:
 *   const [data, error] = await tryCatch(someAsyncFunction)();
 *   if (error) { ... handle error ... }
 */
export function tryCatch(promise) {
  return async (...args) => {
    try {
      const data = await promise(...args);
      return [data, null];
    } catch (error) {
      console.error('Operation failed:', error);
      return [null, error];
    }
  };
}

// Display a success message using the themed SuccessBanner component
export function showSuccess(message, title = 'Success', options = {}) {
  console.log(title, message);
  
  return showSuccessBanner(
    { message },
    {
      title,
      position: 'top-right',
      autoHide: true,
      autoHideDelay: 4000,
      ...options
    }
  );
}

// Convenience functions for different types of errors
export function showNetworkError(error, options = {}) {
  return showError(error, 'Network Error', {
    autoHideDelay: 8000,
    ...options
  });
}

export function showValidationError(error, options = {}) {
  return showError(error, 'Validation Error', {
    autoHideDelay: 6000,
    ...options
  });
}

export function showPermissionError(error, options = {}) {
  return showError(error, 'Permission Error', {
    autoHideDelay: 8000,
    ...options
  });
}

// Convenience functions for different types of success messages
export function showSaveSuccess(message = 'Changes saved successfully', options = {}) {
  return showSuccess(message, 'Saved', {
    autoHideDelay: 3000,
    ...options
  });
}

export function showUploadSuccess(message = 'Upload completed successfully', options = {}) {
  return showSuccess(message, 'Upload Complete', {
    autoHideDelay: 4000,
    ...options
  });
}

export function showDeleteSuccess(message = 'Item deleted successfully', options = {}) {
  return showSuccess(message, 'Deleted', {
    autoHideDelay: 3000,
    ...options
  });
} 